/**
 * Canonical route type aliases for football concepts.
 *
 * Maps informal coach-speak route names (e.g., "fly", "streak", "9")
 * to canonical route types (e.g., "go"). Used in AI concept parsing
 * and output validation.
 */

/** Canonical route types for concept assignments */
export type CanonicalRouteType =
	| 'go' | 'out' | 'in' | 'slant' | 'curl' | 'post' | 'corner' | 'flat'
	| 'cross' | 'shallow_cross' | 'dig' | 'wheel' | 'seam' | 'screen' | 'bubble_screen'
	| 'block' | 'block_down' | 'pull' | 'kick_out' | 'trap' | 'pass_protect'

/** Maps informal route names to canonical types */
export const ROUTE_ALIASES: Record<string, CanonicalRouteType> = {
	// Go/Fly/Streak/9
	'go': 'go', 'fly': 'go', 'streak': 'go', '9': 'go', 'nine': 'go', 'fade': 'go',
	// Out/5
	'out': 'out', '5': 'out', 'five': 'out', 'speed out': 'out',
	// In/Dig/6
	'in': 'in', 'dig': 'in', '6': 'in', 'six': 'in',
	// Slant/2
	'slant': 'slant',
	// Curl/Comeback/Hitch
	'curl': 'curl', 'comeback': 'curl', 'hitch': 'curl',
	// Post/8
	'post': 'post', '8': 'post', 'eight': 'post',
	// Corner/Flag/7
	'corner': 'corner', '7': 'corner', 'seven': 'corner', 'flag': 'corner',
	// Flat/Arrow/1
	'flat': 'flat', 'arrow': 'flat',
	// Cross/Drag
	'cross': 'cross', 'drag': 'cross',
	'shallow': 'shallow_cross', 'shallow cross': 'shallow_cross',
	// Screen
	'screen': 'screen', 'bubble': 'bubble_screen', 'bubble screen': 'bubble_screen',
	// Seam
	'seam': 'seam',
	// Wheel
	'wheel': 'wheel',
	// Blocking
	'block': 'block', 'down block': 'block_down', 'block down': 'block_down',
	'pull': 'pull', 'kick out': 'kick_out', 'trap': 'trap',
	'pass protect': 'pass_protect', 'protect': 'pass_protect',
}

/** All canonical route type names (deduplicated) for prompt injection */
export const CANONICAL_ROUTE_TYPES = [...new Set(Object.values(ROUTE_ALIASES))]

/** Resolve an informal route name to its canonical type */
export function resolveRouteAlias(name: string): CanonicalRouteType | null {
	return ROUTE_ALIASES[name.toLowerCase().trim()] ?? null
}
